import { create } from 'zustand'
import { type PersistOptions, StateStorage, createJSONStorage, persist } from 'zustand/middleware'
import { Mutate, StoreApi } from 'zustand/vanilla'
import { config } from '../appConfig'
import { createAppearanceStore } from './appearance'
import { createBackupSeedStore } from './backupSeed'
import { createBalanceChangesStore } from './balanceChange'
import { createConnectionProviderStore } from './connectionProvider'
import { createContactsStore } from './contacts'
import { createCurrentWalletStore } from './currentWallet'
import { createCustomTokensStore } from './customTokens'
import { createDevModeStore } from './devMode'
import { createEVMChainsInfoStore } from './evmChainsInfo'
import { createEvmSessionsStore } from './evmSessions'
import { createLocalesStore } from './locales'
import { createMigrateToV2Store } from './migrateToV2'
import { createNftBalancesStore } from './nftBalances'
import { createNotificationWindowStore } from './notificationWindow'
import { createPendingRequestsStore } from './pendingRequests'
import { createPermissionsStore } from './permissions'
import { createPerpsSettingsStore } from './perpsSettings'
import { createPromoStore } from './promo'
import { createProtectedCanistersStore } from './protectedCanisters'
import { createSelectedChainStore } from './selectedChain'
import { createSelectedTabStore } from './selectedTab'
import { createSnsStore } from './sns'
import { createSwapSlippageStore } from './swapSlippage'
import { createTokenBalancesStore } from './tokenBalances'
import { createTokenStore } from './tokens'
import { createUserActivityStore } from './userActivity'
import { createUserEVMChainsStore } from './userEVMChains'
import { createUserTokensStore } from './userTokens'
import { createWalletBTCAddressesStore } from './walletBTCAddresses'
import { createWalletConnectSessionsStore } from './walletConnectSessions'
import { createWalletPropertiesStore } from './walletProperties'

type Initializer<TState> = Parameters<typeof persist<TState>>[0]

export type StoreWithPersist<TState> = Mutate<StoreApi<TState>, [['zustand/persist', unknown]]> & {
  initializer: Initializer<TState>
}

export function createStore<TState>(
  storage: StateStorage,
  initializer: Initializer<TState>,
  { persist: persistOptions }: { persist?: PersistOptions<TState> } = {}
) {
  const name = `plug.store.${persistOptions?.name}`
  return Object.assign(
    create(
      persist(initializer, {
        ...persistOptions,
        name,
        storage: persistOptions ? createJSONStorage(() => storage) : undefined,
      })
    ),
    { initializer }
  )
}

const stateStorage: StateStorage = {
  getItem: async (key: string): Promise<string | null> => {
    return (await config.localStorage.get(key)) || null
  },
  setItem: async (key: string, value: string): Promise<void> => {
    await config.localStorage.set(key, value)
  },
  removeItem: async (key: string): Promise<void> => {
    await config.localStorage.remove(key)
  },
}

export const {
  useContacts,
  useCurrentWallet,
  useCustomTokens,
  useTokens,
  useDevMode,
  useNftBalances,
  useNotificationWindows,
  usePendingRequests,
  usePermissions,
  useProtectedCanisters,
  useSelectedTab,
  useSns,
  useSwapSlippage,
  useTokenBalances,
  useUserTokens,
  useWalletBTCAddresses,
  useWalletProperties,
  useBalanceChanges,
  useLocales,
  useMigrateToV2,
  useSelectedChain,
  useUserActivity,
  useBackupSeed,
  usePromo,
  useUserEVMChains,
  useEVMChainsInfo,
  usePerpsSettings,
  useConnectionProvider,
  useWalletConnectSessions,
  useEvmSessions,
  useAppearance,
} = {
  useContacts: createContactsStore(stateStorage),
  useCurrentWallet: createCurrentWalletStore(stateStorage),
  useCustomTokens: createCustomTokensStore(stateStorage),
  useTokens: createTokenStore(stateStorage),
  useDevMode: createDevModeStore(stateStorage),
  useNftBalances: createNftBalancesStore(stateStorage),
  useNotificationWindows: createNotificationWindowStore(stateStorage),
  usePendingRequests: createPendingRequestsStore(stateStorage),
  usePermissions: createPermissionsStore(stateStorage),
  useProtectedCanisters: createProtectedCanistersStore(stateStorage),
  useSelectedTab: createSelectedTabStore(stateStorage),
  useSns: createSnsStore(stateStorage),
  useSwapSlippage: createSwapSlippageStore(stateStorage),
  useTokenBalances: createTokenBalancesStore(stateStorage),
  useUserTokens: createUserTokensStore(stateStorage),
  useWalletBTCAddresses: createWalletBTCAddressesStore(stateStorage),
  useWalletProperties: createWalletPropertiesStore(stateStorage),
  useBalanceChanges: createBalanceChangesStore(stateStorage),
  useLocales: createLocalesStore(stateStorage),
  useMigrateToV2: createMigrateToV2Store(stateStorage),
  useSelectedChain: createSelectedChainStore(stateStorage),
  useUserActivity: createUserActivityStore(stateStorage),
  useBackupSeed: createBackupSeedStore(stateStorage),
  usePromo: createPromoStore(stateStorage),
  useUserEVMChains: createUserEVMChainsStore(stateStorage),
  useEVMChainsInfo: createEVMChainsInfoStore(stateStorage),
  usePerpsSettings: createPerpsSettingsStore(stateStorage),
  useConnectionProvider: createConnectionProviderStore(stateStorage),
  useWalletConnectSessions: createWalletConnectSessionsStore(stateStorage),
  useEvmSessions: createEvmSessionsStore(stateStorage),
  useAppearance: createAppearanceStore(stateStorage),
}
